import {Injectable} from '@angular/core';
import {combineLatest, Observable} from 'rxjs';
import {StaffMember} from '@model/staffMember';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {select, Store} from '@ngrx/store';
import {getCollaborativeWorkPerformances, getCollaborativeWorkProjects, getStaffMemberCollaborativeWorkPerformances, StoreState} from '@redux/reducers';
import {CollaborativeWorkProject} from '@model/collaborative-work-project';
import {CollaborativeWorkService} from '@services/collaborative-work.service';
import {School} from '@model/school';
import {map, switchMap, take} from 'rxjs/operators';
import {
  collaborativeWorkProjectSaved,
  collaborativeWorkProjectsLoaded
} from '@redux/actions/collaborative-work-projects.action';
import {CollaborativeWorkPerformance} from '@model/collaborative-work-performance';
import {
  collaborativeWorkPerformanceCreated,
  collaborativeWorkPerformanceDeleted,
  collaborativeWorkPerformanceReset,
  collaborativeWorkPerformancesLoaded
} from '@redux/actions/collaborative-work-performances.action';
import {Profile} from '@model/profile';
import {
  collaborativeWorkPerformancesByStaffmemberLoaded
} from '@redux/actions/collaborative-work-performance-by-staffmembers.action';
import {StaffMemberCollaborativeWorkPerformance} from '@model/staffMember-collaborative-work-performance';

@Injectable()
export class CollaborativeWorkFacadeService {

  school$: Observable<School>;
  staffMembers$: Observable<StaffMember[]>;
  collaborativeWorkProjects$: Observable<CollaborativeWorkProject[]>;
  collaborativeWorkPerformances$: Observable<CollaborativeWorkPerformance[]>;
  profile$: Observable<Profile>;
  staffMemberCollaborativeWorkPerformances$: Observable<StaffMemberCollaborativeWorkPerformance[]>;

  constructor(private _store: Store<StoreState>,
              private _sharedFacade: SharedFacadeService,
              private _collaborativeWorkService: CollaborativeWorkService) {
    this.staffMembers$ = this._sharedFacade.staffMembers$;
    this.school$ = this._sharedFacade.school$;
    this.collaborativeWorkProjects$ = this._store.pipe(
      select(getCollaborativeWorkProjects)
    );
    this.collaborativeWorkPerformances$ = this._store.pipe(
      select(getCollaborativeWorkPerformances)
    );
    this.profile$ = this._sharedFacade.profile$;
    this.staffMemberCollaborativeWorkPerformances$ = this._store.pipe(
      select(getStaffMemberCollaborativeWorkPerformances)
    );
  }

  loadStaffMembers() {
    this._sharedFacade.loadStaffMembers();
  }

  saveCollaborativeWorkProject(project: CollaborativeWorkProject) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._collaborativeWorkService.saveCollaborativeWorkProject(school.id, project))
    ).subscribe(project => this._store.dispatch(collaborativeWorkProjectSaved({collaborativeProject: project})));
  }

  loadCollaborativeWorkProject(year?: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._collaborativeWorkService.loadCollaborativeWorkProjects(school.id, year ? year : school.activeSchoolYear))
    ).subscribe(projects => this._store.dispatch(collaborativeWorkProjectsLoaded({collaborativeProjects: projects})));
  }

  loadCollaborativeWorkProjectWithDuration(year?: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._collaborativeWorkService.loadCollaborativeWorkProjects(school.id, year ? year : school.activeSchoolYear, true))
    ).subscribe(projects => this._store.dispatch(collaborativeWorkProjectsLoaded({collaborativeProjects: projects})));
  }

  loadCollaborativeWorkPerformances(staffMemberId?: number, schoolYear?: number) {
    combineLatest([this.school$, this.profile$]).pipe(
      take(1),
      switchMap(([school, profile]) => this._collaborativeWorkService.loadCollaborativeWorkPerformances(school.id, staffMemberId ? staffMemberId : profile.id, schoolYear ? schoolYear : school.activeSchoolYear))
    ).subscribe(performances => this._store.dispatch(collaborativeWorkPerformancesLoaded({collaborativePerformances: performances})));
  }

  loadCollaborativeWorkPerformancesByProject(projectId?: number) {
    this.school$.pipe(
      take(1),
      switchMap((school) => this._collaborativeWorkService.loadCollaborativeWorkPerformancesByProject(school.id, projectId))
    ).subscribe(performances => this._store.dispatch(collaborativeWorkPerformancesLoaded({collaborativePerformances: performances})));
  }

  saveCollaborativeWorkPerformance(collaborativeWorkPerformance: CollaborativeWorkPerformance) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._collaborativeWorkService.savePerformance(school.id, collaborativeWorkPerformance.projectId, collaborativeWorkPerformance)),
      map(performanceSaved =>
        Object.assign({}, performanceSaved, {
          projectCode: collaborativeWorkPerformance.projectCode,
          projectId: collaborativeWorkPerformance.projectId
        }))
    ).subscribe(performance => this._store.dispatch(collaborativeWorkPerformanceCreated({collaborativePerformance: performance})));
  }

  deleteCollaborativeWorkPerformance(performance: CollaborativeWorkPerformance) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._collaborativeWorkService.deletePerformance(school.id, performance.projectId, performance.id))
    ).subscribe(() => this._store.dispatch(collaborativeWorkPerformanceDeleted({performanceId: performance.id})));
  }

  loadStaffMemberCollaborativeWorkProjectPerformances(schoolYear?: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._collaborativeWorkService.loadStaffMemberCollaborativeWorkProjectPerformances(school.id, schoolYear ? schoolYear : school.activeSchoolYear))
    ).subscribe(staffMemberCollaborativeWorkPerformances =>
      this._store.dispatch(collaborativeWorkPerformancesByStaffmemberLoaded({staffMemberCollaborativeWorkPerformances})));
  }

  resetCollaborativeWorkProjectPerformances() {
    this._store.dispatch(collaborativeWorkPerformanceReset());
  }
}
